import { map } from "lodash";
import { aiccInstance } from "utils/instance";
import {
  AiccCanDoStatementUploadPreviewResponse,
  AiccCanDoStatementVersionsResponse,
  AiccDefaultScenarioVersionsResponse,
  AiccLanguageNameResponse,
  AiccLessonEntryUploadPreviewResponse,
  AiccLessonEntryVersionsResponse,
  AiccLessonInformationUploadPreviewResponse,
  AiccLessonInformationVersionsResponse,
  AiccLessonReviewScriptUploadPreviewResponse,
  AiccLessonReviewScriptVersionsResponse,
  AiccScenarioCategoryVersionsResponse,
  AiccSttModelResponse,
  AiccSttSettingVersionsResponse,
  AiccTtsPreviewResponse,
  AiccTtsProviderVoiceResponse,
  AiccTtsSettingVersionsResponse,
  AiccTtsVoiceResolutionResponse,
  AiccUiElementTranslationVersionsResponse,
} from "types/dto/response/aiccLearningAssets";
import {
  AICC_TTS_PROVIDER,
  AiccAssetApi,
  AiccCanDoStatementApi,
  AiccDefaultScenario,
  AiccDefaultScenarioPayload,
  AiccLessonEntryApi,
  AiccLessonInformationApi,
  AiccLessonRef,
  AiccLessonReviewScriptApi,
  AiccScenarioCategory,
  AiccScenarioCategoryPayload,
  AiccSttSettingApi,
  AiccTtsSettingApi,
  AiccUiElementTranslationApi,
} from "types/model/aiccLearningAssets";
import {
  toAiccCanDoStatementReplaceRequest,
  toAiccCanDoStatementRequest,
  toAiccDefaultScenarioCreateRequest,
  toAiccDefaultScenarioUpdateRequest,
  toAiccLessonEntryReplaceRequest,
  toAiccLessonEntryRequest,
  toAiccLessonEntryUpdateRequest,
  toAiccLessonInformationReplaceRequest,
  toAiccLessonInformationRequest,
  toAiccLessonReviewScriptReplaceRequest,
  toAiccScenarioCategoryCreateRequest,
  toAiccScenarioCategoryUpdateRequest,
  toAiccSttSettingUpdateRequest,
  toAiccTtsPreviewRequest,
  toAiccTtsSettingUpdateRequest,
  toAiccUiElementTranslationUpdateRequest,
  transformAiccCanDoStatementUploadPreview,
  transformAiccCanDoStatementVersions,
  transformAiccDefaultScenarioVersions,
  transformAiccLanguageName,
  transformAiccLessonEntryUploadPreview,
  transformAiccLessonEntryVersions,
  transformAiccLessonInformationUploadPreview,
  transformAiccLessonInformationVersions,
  transformAiccLessonReviewScriptUploadPreview,
  transformAiccLessonReviewScriptVersions,
  transformAiccScenarioCategoryVersions,
  transformAiccSttModel,
  transformAiccSttSettingVersions,
  transformAiccTtsSettingVersions,
  transformAiccUiElementTranslationVersions,
} from "types/transformers/aiccLearningAssets";

function toFileFormData(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return formData;
}

export async function fetchAiccLanguageNames() {
  return aiccInstance
    .get<AiccLanguageNameResponse[]>("/languages/names")
    .then((response) => map(response ?? [], transformAiccLanguageName));
}

const defaultScenarioUrl = (languageId: number) => `/languages/${languageId}/default-scenarios`;

export const aiccDefaultScenarioApi: AiccAssetApi<AiccDefaultScenario, AiccDefaultScenarioPayload> = {
  fetchVersions: (languageId) =>
    aiccInstance
      .get<AiccDefaultScenarioVersionsResponse>(defaultScenarioUrl(languageId))
      .then(transformAiccDefaultScenarioVersions),
  create: (languageId, payload) =>
    aiccInstance
      .post<AiccDefaultScenarioVersionsResponse>(
        defaultScenarioUrl(languageId),
        toAiccDefaultScenarioCreateRequest(payload)
      )
      .then(transformAiccDefaultScenarioVersions),
  update: (languageId, scenarioId, payload) =>
    aiccInstance
      .put<AiccDefaultScenarioVersionsResponse>(
        `${defaultScenarioUrl(languageId)}/${scenarioId}`,
        toAiccDefaultScenarioUpdateRequest(payload)
      )
      .then(transformAiccDefaultScenarioVersions),
  remove: (languageId, scenarioId) =>
    aiccInstance
      .delete<AiccDefaultScenarioVersionsResponse>(`${defaultScenarioUrl(languageId)}/${scenarioId}`)
      .then(transformAiccDefaultScenarioVersions),
};

const scenarioCategoryUrl = (languageId: number) => `/languages/${languageId}/scenario-categories`;

export const aiccScenarioCategoryApi: AiccAssetApi<AiccScenarioCategory, AiccScenarioCategoryPayload> = {
  fetchVersions: (languageId) =>
    aiccInstance
      .get<AiccScenarioCategoryVersionsResponse>(scenarioCategoryUrl(languageId))
      .then(transformAiccScenarioCategoryVersions),
  create: (languageId, payload) =>
    aiccInstance
      .post<AiccScenarioCategoryVersionsResponse>(
        scenarioCategoryUrl(languageId),
        toAiccScenarioCategoryCreateRequest(payload)
      )
      .then(transformAiccScenarioCategoryVersions),
  update: (languageId, categoryId, payload) =>
    aiccInstance
      .put<AiccScenarioCategoryVersionsResponse>(
        `${scenarioCategoryUrl(languageId)}/${categoryId}`,
        toAiccScenarioCategoryUpdateRequest(payload)
      )
      .then(transformAiccScenarioCategoryVersions),
  remove: (languageId, categoryId) =>
    aiccInstance
      .delete<AiccScenarioCategoryVersionsResponse>(`${scenarioCategoryUrl(languageId)}/${categoryId}`)
      .then(transformAiccScenarioCategoryVersions),
};

const sttSettingUrl = (languageId: number) => `/languages/${languageId}/stt-settings`;

export const aiccSttSettingApi: AiccSttSettingApi = {
  fetchVersions: (languageId) =>
    aiccInstance.get<AiccSttSettingVersionsResponse>(sttSettingUrl(languageId)).then(transformAiccSttSettingVersions),
  update: (languageId, payload) =>
    aiccInstance
      .put<AiccSttSettingVersionsResponse>(sttSettingUrl(languageId), toAiccSttSettingUpdateRequest(payload))
      .then(transformAiccSttSettingVersions),
};

/**
 * The STT engine catalogue — every engine the AGENT can build, with its mode, its prose, which slot it is
 * the default for and whether it is on its way out.
 *
 * 🔴 GLOBAL, NOT PER LANGUAGE, and not this repo's to list. `SttSetting/configs.tsx` used to hold four
 * engines with hand-written names: it offered one the backend had RETIRED and was missing the two that
 * replace it, so a language configured on either printed a raw id. One source, read once.
 */
export async function fetchAiccSttModels() {
  return aiccInstance
    .get<AiccSttModelResponse[]>("/stt/models")
    .then((response) => map(response, transformAiccSttModel));
}

const ttsSettingUrl = (languageId: number) => `/languages/${languageId}/tts-settings`;

export async function fetchAiccTtsVoices() {
  return aiccInstance.get<AiccTtsProviderVoiceResponse[]>(`/tts/providers/${AICC_TTS_PROVIDER}/voices`);
}

export async function fetchAiccTtsVoiceResolution(voiceId: string) {
  return aiccInstance.get<AiccTtsVoiceResolutionResponse>(`/tts/voices/${encodeURIComponent(voiceId)}/resolution`);
}

export const aiccTtsSettingApi: AiccTtsSettingApi = {
  fetchVersions: (languageId) =>
    aiccInstance.get<AiccTtsSettingVersionsResponse>(ttsSettingUrl(languageId)).then(transformAiccTtsSettingVersions),
  update: (languageId, rows, selectedModel, savedModel) =>
    aiccInstance
      .put<AiccTtsSettingVersionsResponse>(
        ttsSettingUrl(languageId),
        toAiccTtsSettingUpdateRequest(rows, selectedModel, savedModel)
      )
      .then(transformAiccTtsSettingVersions),
  synthesize: (languageId, payload) =>
    aiccInstance
      .post<AiccTtsPreviewResponse>("/tts/synthesize/calibrate", toAiccTtsPreviewRequest(languageId, payload))
      .then((response) => response.audio_url ?? ""),
};

const uiElementTranslationUrl = (languageId: number) => `/languages/${languageId}/ui-element-translations`;

export const aiccUiElementTranslationApi: AiccUiElementTranslationApi = {
  fetchVersions: (languageId) =>
    aiccInstance
      .get<AiccUiElementTranslationVersionsResponse>(uiElementTranslationUrl(languageId))
      .then(transformAiccUiElementTranslationVersions),
  update: (languageId, rows) =>
    aiccInstance
      .put<AiccUiElementTranslationVersionsResponse>(
        uiElementTranslationUrl(languageId),
        toAiccUiElementTranslationUpdateRequest(rows)
      )
      .then(transformAiccUiElementTranslationVersions),
};

const vocabularyUrl = (languageId: number) => `/languages/${languageId}/vocabulary`;

const vocabularyItemsUrl = (languageId: number, { level, lesson }: AiccLessonRef) =>
  `${vocabularyUrl(languageId)}/level/${level}/lesson/${lesson}/items`;

export const aiccVocabularyApi: AiccLessonEntryApi = {
  fetchVersions: (languageId) =>
    aiccInstance.get<AiccLessonEntryVersionsResponse>(vocabularyUrl(languageId)).then(transformAiccLessonEntryVersions),
  create: (languageId, payload) =>
    aiccInstance
      .post<AiccLessonEntryVersionsResponse>(vocabularyItemsUrl(languageId, payload), toAiccLessonEntryRequest(payload))
      .then(transformAiccLessonEntryVersions),
  update: (languageId, ref, payload) =>
    aiccInstance
      .put<AiccLessonEntryVersionsResponse>(
        `${vocabularyItemsUrl(languageId, ref)}/${ref.index}`,
        toAiccLessonEntryUpdateRequest(payload)
      )
      .then(transformAiccLessonEntryVersions),
  remove: (languageId, ref) =>
    aiccInstance
      .delete<AiccLessonEntryVersionsResponse>(`${vocabularyItemsUrl(languageId, ref)}/${ref.index}`)
      .then(transformAiccLessonEntryVersions),
  validateUpload: (languageId, file) =>
    aiccInstance
      .post<AiccLessonEntryUploadPreviewResponse>(`${vocabularyUrl(languageId)}/upload`, toFileFormData(file))
      .then(transformAiccLessonEntryUploadPreview),
  replaceAll: (languageId, items) =>
    aiccInstance
      .put<AiccLessonEntryVersionsResponse>(vocabularyUrl(languageId), toAiccLessonEntryReplaceRequest(items))
      .then(transformAiccLessonEntryVersions),
  exportFile: (languageId, status) =>
    aiccInstance.get<Blob>(`${vocabularyUrl(languageId)}/export`, { params: { status }, responseType: "blob" }),
};

const lessonReviewScriptUrl = (languageId: number) => `/languages/${languageId}/lesson-review-scripts`;

export const aiccLessonReviewScriptApi: AiccLessonReviewScriptApi = {
  fetchVersions: (languageId) =>
    aiccInstance
      .get<AiccLessonReviewScriptVersionsResponse>(lessonReviewScriptUrl(languageId))
      .then(transformAiccLessonReviewScriptVersions),
  validateUpload: (languageId, file) =>
    aiccInstance
      .post<AiccLessonReviewScriptUploadPreviewResponse>(
        `${lessonReviewScriptUrl(languageId)}/upload`,
        toFileFormData(file)
      )
      .then(transformAiccLessonReviewScriptUploadPreview),
  replaceAll: (languageId, lessons) =>
    aiccInstance
      .put<AiccLessonReviewScriptVersionsResponse>(
        lessonReviewScriptUrl(languageId),
        toAiccLessonReviewScriptReplaceRequest(lessons)
      )
      .then(transformAiccLessonReviewScriptVersions),
};

const canDoStatementUrl = (languageId: number) => `/languages/${languageId}/can-do-statements`;

export const aiccCanDoStatementApi: AiccCanDoStatementApi = {
  fetchVersions: (languageId) =>
    aiccInstance
      .get<AiccCanDoStatementVersionsResponse>(canDoStatementUrl(languageId))
      .then(transformAiccCanDoStatementVersions),
  create: (languageId, payload) =>
    aiccInstance
      .post<AiccCanDoStatementVersionsResponse>(canDoStatementUrl(languageId), toAiccCanDoStatementRequest(payload))
      .then(transformAiccCanDoStatementVersions),
  update: (languageId, statementId, payload) =>
    aiccInstance
      .put<AiccCanDoStatementVersionsResponse>(
        `${canDoStatementUrl(languageId)}/${statementId}`,
        toAiccCanDoStatementRequest(payload)
      )
      .then(transformAiccCanDoStatementVersions),
  remove: (languageId, statementId) =>
    aiccInstance
      .delete<AiccCanDoStatementVersionsResponse>(`${canDoStatementUrl(languageId)}/${statementId}`)
      .then(transformAiccCanDoStatementVersions),
  validateUpload: (languageId, file) =>
    aiccInstance
      .post<AiccCanDoStatementUploadPreviewResponse>(`${canDoStatementUrl(languageId)}/upload`, toFileFormData(file))
      .then(transformAiccCanDoStatementUploadPreview),
  replaceAll: (languageId, rows) =>
    aiccInstance
      .put<AiccCanDoStatementVersionsResponse>(canDoStatementUrl(languageId), toAiccCanDoStatementReplaceRequest(rows))
      .then(transformAiccCanDoStatementVersions),
};

const lessonInformationUrl = (languageId: number) => `/languages/${languageId}/lesson-info`;

const lessonInformationLessonUrl = (languageId: number, { level, lesson }: AiccLessonRef) =>
  `${lessonInformationUrl(languageId)}/level/${level}/lesson/${lesson}`;

export const aiccLessonInformationApi: AiccLessonInformationApi = {
  fetchVersions: (languageId) =>
    aiccInstance
      .get<AiccLessonInformationVersionsResponse>(lessonInformationUrl(languageId))
      .then(transformAiccLessonInformationVersions),
  update: (languageId, ref, payload) =>
    aiccInstance
      .put<AiccLessonInformationVersionsResponse>(
        lessonInformationLessonUrl(languageId, ref),
        toAiccLessonInformationRequest(payload)
      )
      .then(transformAiccLessonInformationVersions),
  validateUpload: (languageId, file) =>
    aiccInstance
      .post<AiccLessonInformationUploadPreviewResponse>(
        `${lessonInformationUrl(languageId)}/upload`,
        toFileFormData(file)
      )
      .then(transformAiccLessonInformationUploadPreview),
  replaceAll: (languageId, lessons) =>
    aiccInstance
      .put<AiccLessonInformationVersionsResponse>(
        lessonInformationUrl(languageId),
        toAiccLessonInformationReplaceRequest(lessons)
      )
      .then(transformAiccLessonInformationVersions),
};
